import { defineMessages } from 'react-intl';

export const simplifiedGettingStartedMessages = defineMessages({
  'whatChanged:question': {
    id: 'simplifiedHelpContent.gettingStarted.whatChanged.question',
    defaultMessage: 'What changed about the sign-in page?',
  },
  'whatChanged:answer': {
    id: 'simplifiedHelpContent.gettingStarted.whatChanged.answer',
    defaultMessage:
      'The sign-in page now asks for your email address first, rather than presenting a list of sign-in options to choose from. After you enter your email and select Continue, the system identifies the correct sign-in method for your account and takes you there automatically.',
  },
  'howSystemChooses:question': {
    id: 'simplifiedHelpContent.gettingStarted.howSystemChooses.question',
    defaultMessage: 'How does the system choose my sign-in method for me?',
  },
  'howSystemChooses:answer': {
    id: 'simplifiedHelpContent.gettingStarted.howSystemChooses.answer',
    defaultMessage:
      "The system chooses your sign-in method based on your email address and account configuration. If you have an existing account with that email that uses AWS Builder ID, you are redirected to AWS Builder ID. If your email domain has organizational single sign-on (SSO) configured, you are directed to your organization's sign-in page.",
  },
});

export const simplifiedSigningInMessages = defineMessages({
  'howToSignIn:question': {
    id: 'simplifiedHelpContent.signingIn.howToSignIn.question',
    defaultMessage: 'How do I sign in with my existing account?',
  },
  'howToSignIn:answer': {
    id: 'simplifiedHelpContent.signingIn.howToSignIn.answer',
    defaultMessage:
      'Enter the email address associated with your AWS Training and Certification account on the sign-in page and select Continue. The system will recognize your account and redirect you to the correct sign-in method. For example, if you have both an AWS Builder ID account and an organizational single sign-on account associated with the same email, you will see a screen asking you to choose which one to use. If you previously signed in using Google, Apple, GitHub, or Amazon, you can also select the corresponding button directly on the sign-in page.',
  },
  'multipleAccounts:question': {
    id: 'simplifiedHelpContent.signingIn.multipleAccounts.question',
    defaultMessage:
      'I have multiple accounts. How do I make sure I sign in to the right one?',
  },
  'multipleAccounts:answer': {
    id: 'simplifiedHelpContent.signingIn.multipleAccounts.answer',
    defaultMessage:
      'Enter the email address associated with the account you want to access. If the system finds more than one account linked to that email, it presents a selection screen so you can choose the correct sign-in method. Using the same email address each time is the most reliable way to reach the right account and keep your training history intact.',
  },
  'previousSignIn:question': {
    id: 'simplifiedHelpContent.signingIn.previousSignIn.question',
    defaultMessage:
      'I previously signed in to other AWS Training and Certification experiences. Can I use the same sign-in method?',
  },
  'previousSignIn:answer': {
    id: 'simplifiedHelpContent.signingIn.previousSignIn.answer',
    defaultMessage:
      'Yes. Use the same sign-in email you used before to access all your learning history and data under one profile. This ensures your certifications, course progress, and achievements remain on a single account.',
  },
});

export const simplifiedNewUsersMessages = defineMessages({
  'howToSignUp:question': {
    id: 'simplifiedHelpContent.newUsers.howToSignUp.question',
    defaultMessage:
      "I don't have an AWS training and certification account. How do I sign up?",
  },
  'howToSignUp:answer': {
    id: 'simplifiedHelpContent.newUsers.howToSignUp.answer',
    defaultMessage:
      "Enter your email address on the sign-in page and select Continue. If no account exists for your email, the system automatically starts the sign-up process. <ul><li>If your email address belongs to an organization that has enabled organization single sign-on to enable you to use your work credentials for Training and Certification, you will be directed to your company's sign-in page to authenticate and create your account.</li><li>If your email address is a personal email or does not have organizational single sign-on setup, you will be directed to AWS Builder ID to authenticate.</li></ul>",
  },
  'needAwsAccount:question': {
    id: 'simplifiedHelpContent.newUsers.needAwsAccount.question',
    defaultMessage:
      'Do I need an AWS account to sign up for AWS Training and Certification?',
  },
  'needAwsAccount:answer': {
    id: 'simplifiedHelpContent.newUsers.needAwsAccount.answer',
    defaultMessage:
      'No, AWS account is not a sign-in method for AWS Training and Certification products. Learners can sign up through <builderID>AWS Builder ID</builderID>, which is a separate, free account specifically for accessing AWS tools and training. Refer to <builderID>AWS Builder ID</builderID> for more details.',
  },
});

export const simplifiedSocialSignInMessages = defineMessages({
  'howSocialWorks:question': {
    id: 'simplifiedHelpContent.social.howSocialWorks.question',
    defaultMessage: 'How does signing in with social login work?',
  },
  'howSocialWorks:answer': {
    id: 'simplifiedHelpContent.social.howSocialWorks.answer',
    defaultMessage:
      "Selecting the Google, GitHub, Amazon, or Apple button on the sign-in page takes you directly to that provider's sign-in page. Once you authenticate, you are signed in to AWS Training and Certification. On your first sign-in, an AWS Builder ID account is created and linked to your social login automatically, so you do not need to create or manage a Builder ID separately.",
  },
  'appleHiddenEmail:question': {
    id: 'simplifiedHelpContent.social.appleHiddenEmail.question',
    defaultMessage:
      'I used Apple sign-in and chose to hide my email address. How do I sign in again?',
  },
  'appleHiddenEmail:answer': {
    id: 'simplifiedHelpContent.social.appleHiddenEmail.answer',
    defaultMessage:
      'When you hide your email with Apple, Apple provides a private relay email address instead of your real one. Use the Continue with Apple button on the sign-in page when accessing AWS Training and Certification experiences. You are not required to type the private relay email on the sign-in page to access your account. Use the same Apple account to authenticate and access the existing account created with Apple sign-in.',
  },
  'socialAccountExists:question': {
    id: 'simplifiedHelpContent.social.socialAccountExists.question',
    defaultMessage:
      'When I use a social login, I get an error that the account already exists?',
  },
  'socialAccountExists:answer': {
    id: 'simplifiedHelpContent.social.socialAccountExists.answer',
    defaultMessage:
      'Social login options are powered by AWS Builder ID. When you select a social login option, the system attempts to sign you in to or create an AWS Builder ID account. If an existing Builder ID account is already associated with that email address but is not linked to the social login method you selected, you will see this error. To resolve it, return to the sign-in page, enter your email address, and sign in using your existing Builder ID account.',
  },
});

export const simplifiedOrgSSOMessages = defineMessages({
  'whyRedirected:question': {
    id: 'simplifiedHelpContent.orgSSO.whyRedirected.question',
    defaultMessage: "Why was I redirected to my company's sign-in page?",
  },
  'whyRedirected:answer': {
    id: 'simplifiedHelpContent.orgSSO.whyRedirected.answer',
    defaultMessage:
      "Many organizations configure a central sign-in system that lets their employees access AWS Training and Certification using their work credentials. When you enter an email address that belongs to a domain configured for organizational single sign-on, you will be automatically redirected to your organization's sign-in page.",
  },
  'noPermission:question': {
    id: 'simplifiedHelpContent.orgSSO.noPermission.question',
    defaultMessage:
      "I was sent to my company's sign-in page but I don't have permission to complete sign-in. What do I do?",
  },
  'noPermission:answer': {
    id: 'simplifiedHelpContent.orgSSO.noPermission.answer',
    defaultMessage:
      "If you cannot complete sign-in through your company's system, contact your IT team or learning administrator to request the necessary permissions.",
  },
});

export const simplifiedTroubleshootingMessages = defineMessages({
  'noAccess:question': {
    id: 'simplifiedHelpContent.troubleshooting.noAccess.question',
    defaultMessage: "I signed in but the page says I don't have access.",
  },
  'noAccess:answer': {
    id: 'simplifiedHelpContent.troubleshooting.noAccess.answer',
    defaultMessage:
      "Some content requires either a paid subscription or to be logged in using your organization email for Partner content before you can access it. If you purchased a subscription and still see an access error, verify you are signed in using the same email and sign-in method you used at purchase. Using a different email or sign-in method creates a separate account without your subscription. If you're attempting to access a Partner course, ensure you're logging in using your company email address. If your organization purchased a subscription on your behalf, contact your learning administrator to request access. If you have verified your sign-in method and continue to have issues, contact <supportLink>AWS Training and Certification customer support.</supportLink>",
  },
  'forgotEmail:question': {
    id: 'simplifiedHelpContent.troubleshooting.forgotEmail.question',
    defaultMessage:
      "I can't remember which email address I used to create my account.",
  },
  'forgotEmail:answer': {
    id: 'simplifiedHelpContent.troubleshooting.forgotEmail.answer',
    defaultMessage:
      'Check any email inboxes you may have used and search for a welcome or confirmation email from AWS Training and Certification. If you are still unable to locate your account, contact <supportLink>AWS Training and Certification customer support</supportLink> for assistance.',
  },
  'browserSupport:question': {
    id: 'simplifiedHelpContent.troubleshooting.browserSupport.question',
    defaultMessage: 'Which browsers work with the new sign-in page?',
  },
  'browserSupport:answer': {
    id: 'simplifiedHelpContent.troubleshooting.browserSupport.answer',
    defaultMessage:
      'The AWS Training and Certification experience works best on the two most recent major versions of: <ul><li>Google Chrome</li><li>Mozilla Firefox</li><li>Microsoft Edge</li><li>Safari</li></ul>',
  },
  'resetPassword:question': {
    id: 'simplifiedHelpContent.troubleshooting.resetPassword.question',
    defaultMessage: 'How do I reset my password?',
  },
  'resetPassword:answer': {
    id: 'simplifiedHelpContent.troubleshooting.resetPassword.answer',
    defaultMessage:
      'Please follow the below instructions based on your sign-in method. <h4>AWS Builder ID</h4> Please visit <builderIDSignInIssue>AWS Builder ID</builderIDSignInIssue> for further assistance. <h4>Organization SSO</h4> Reach out to your learning admin for assistance.',
  },
  'apnDeprecated:question': {
    id: 'simplifiedHelpContent.troubleshooting.apnDeprecated.question',
    defaultMessage:
      'I used the AWS Partner Network sign-in method to access AWS Training and Certification. What should I do now?',
  },
  'apnDeprecated:answer': {
    id: 'simplifiedHelpContent.troubleshooting.apnDeprecated.answer',
    defaultMessage:
      'AWS Partner Network (APN) sign-in is no longer supported as of February 3, 2026. To access your account, enter the same email address you used with APN on the sign-in page. You will be redirected to AWS Builder ID for authentication. After you sign in, your training history from your APN account is automatically merged into your AWS Builder ID account.',
  },
  'changedEmployer:question': {
    id: 'simplifiedHelpContent.troubleshooting.changedEmployer.question',
    defaultMessage:
      'I changed employers (or I left my previous job), how do I make sure the new account has history from older account?',
  },
  'changedEmployer:answer': {
    id: 'simplifiedHelpContent.troubleshooting.changedEmployer.answer',
    defaultMessage:
      'If your older account is enabled by AWS Builder ID and you can still access the AWS Builder ID account, log in to AWS Builder ID and update the email address to your new employer email before creating a separate account with your new employer email. If you do not have access to your older account or it is enabled by Organization single sign-on, please contact <supportLink>AWS Training and Certification customer support</supportLink> for assistance.',
  },
  'lostAccess:question': {
    id: 'simplifiedHelpContent.troubleshooting.lostAccess.question',
    defaultMessage:
      'I lost access to the email or work credentials associated with my account. How do I recover my training history?',
  },
  'lostAccess:answer': {
    id: 'simplifiedHelpContent.troubleshooting.lostAccess.answer',
    defaultMessage:
      'If you can no longer access the email address or work credentials used to sign in to your AWS Training and Certification account, contact <supportLink>AWS Training and Certification customer support.</supportLink> The support team can help transfer your training history (including certifications and course progress) to another account you own.',
  },
  'orgAdoptedSSO:question': {
    id: 'simplifiedHelpContent.troubleshooting.orgAdoptedSSO.question',
    defaultMessage:
      'My organization recently adopted single sign-on. I previously used AWS Builder ID through my organization. What happens to my account?',
  },
  'orgAdoptedSSO:answer': {
    id: 'simplifiedHelpContent.troubleshooting.orgAdoptedSSO.answer',
    defaultMessage:
      "When you enter the email address associated with your organization's account, you will be redirected to your organization's sign-in page instead of AWS Builder ID. After you authenticate with your work credentials, you will access the same account and can continue your learning where you left off.",
  },
  'orgInvitedSSO:question': {
    id: 'simplifiedHelpContent.troubleshooting.orgInvitedSSO.question',
    defaultMessage:
      'I have an individual AWS Builder ID account created with my work email. My organization has invited me to join with single sign-on enabled. What happens to my account?',
  },
  'orgInvitedSSO:answer': {
    id: 'simplifiedHelpContent.troubleshooting.orgInvitedSSO.answer',
    defaultMessage:
      "When you enter your work email, you will be redirected to your organization's sign-in page. After you authenticate with your work credentials, you will get the option to either convert your individual account to an organizational account (so you can continue learning with the same account) or keep your individual account separate.",
  },
  'continueWithAmazon:question': {
    id: 'simplifiedHelpContent.troubleshooting.continueWithAmazon.question',
    defaultMessage:
      'How is the current "Continue with Amazon" sign-in option different from the "Login with Amazon" method that was available earlier?',
  },
  'continueWithAmazon:answer': {
    id: 'simplifiedHelpContent.troubleshooting.continueWithAmazon.answer',
    defaultMessage:
      'Previously, you used your Amazon shopping account to create an AWS Training and Certification account directly. Now, selecting "Continue with Amazon" uses your Amazon shopping account to create or sign in to an AWS Builder ID, which is the account that grants access to AWS Training and Certification. If you had training history associated with your earlier "Login with Amazon" account, it will be available under your AWS Builder ID account with the same email address.',
  },
});

export const simplifiedStillNeedHelpMessages = defineMessages({
  'stillNeedHelp:question': {
    id: 'simplifiedHelpContent.stillNeedHelp.question',
    defaultMessage: 'Still need help?',
  },
  'stillNeedHelp:answer': {
    id: 'simplifiedHelpContent.stillNeedHelp.answer',
    defaultMessage:
      'If your issue is not covered above, contact <supportLink>AWS Training and Certification customer support.</supportLink> When you reach out, include the email address you used to sign in and a description of the error or step where you got stuck. This helps the support team resolve your issue faster.',
  },
});
